import { message } from 'antd';
import { useCallback, useRef } from 'react';
import { createAdminProductWithImages, fetchAdminProductOptions } from '../../services';
import type { AdminProductFormValues, MaterialIconOption, UseAdminProductCreateArgs} from '../interfaces';
import { getDefaultMaterialValues, validateCreateAdminProductValues } from './adminProductForm.utils';
import { getApiErrorMessage } from '../../utils/apiError';

export const useAdminProductCreate = ({
  form,
  sizes,
  materialTemplates,
  setTypes,
  setSizes,
  setMaterialTemplates,
  setLoadingOptions,
  setSubmitting,
  fetchProducts,
}: UseAdminProductCreateArgs) => {
  const productTypesRef = useRef<Parameters<typeof validateCreateAdminProductValues>[2]>([]);
  const resetCreateForm = useCallback((templates: MaterialIconOption[]) => {
    form.resetFields();
    form.setFieldsValue({
      gender: 'unisex',
      price: 0,
      materials: getDefaultMaterialValues(templates.slice(0, 4)),
    });
  }, [form]);

  const fetchOptions = useCallback(async () => {
    setLoadingOptions(true);
    try {
      const response = await fetchAdminProductOptions();
      const defaultMaterials = response.materialIcons.slice(0, 4);
      productTypesRef.current = response.productTypes;
      setTypes(response.productTypes);
      setSizes(response.sizes);
      setMaterialTemplates(response.materialIcons);
      resetCreateForm(defaultMaterials);
    } catch {
      message.error('Failed to load form options.');
    } finally {
      setLoadingOptions(false);
    }
  }, [resetCreateForm, setLoadingOptions, setMaterialTemplates, setSizes, setTypes]);

  const handleCreateProduct = useCallback(async (values: AdminProductFormValues) => {
    setSubmitting(true);
    try {
      validateCreateAdminProductValues(values, sizes, productTypesRef.current);
      await createAdminProductWithImages(values, sizes);
      message.success('Product created successfully.');
      resetCreateForm(materialTemplates);
      await fetchProducts({ append: false, skip: 0 });
    } catch (error: any) {
      message.error(getApiErrorMessage(error, 'Failed to create product.'));
    } finally {
      setSubmitting(false);
    }
  }, [fetchProducts, materialTemplates, resetCreateForm, setSubmitting, sizes]);

  return {
    fetchOptions,
    handleCreateProduct,
  };
};
